import { useCallback } from "react";
import { useDispatch } from "react-redux";
import "./styles.scss";
import type { Product } from "@b2bportal/purchase-api";
import { useExperiment } from "@hopper-b2b/experiments";
import { shopActions } from "@hopper-b2b/flights";
import { useI18nContext } from "@hopper-b2b/i18n";
import { getDarkModePreferred } from "@hopper-b2b/themes";
import type { DisruptionProductType } from "@hopper-b2b/types";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@material-ui/core";
import clsx from "clsx";
import { FEATURE_FLAGS } from "../../../App";
import { darkModeIcons, lightModeIcons } from "../../Body/sharedIcons";

interface FlightFintechSelectionOptionProps {
  title: string;
  description: string;
  checked?: boolean;
  disabled: boolean;
  useCheckbox?: boolean;
  onClick?: () => void;
  setChecked?: (checked: boolean) => void;
  iconName?: string;
  pricePerPax?: string;
  openInfoSheet: () => void;
  product?: Product | DisruptionProductType;
}

export const FlightFintechSelectionOption = ({
  title,
  product,
  description,
  checked,
  disabled,
  useCheckbox = true,
  onClick,
  setChecked,
  iconName,
  pricePerPax,
  openInfoSheet,
}: FlightFintechSelectionOptionProps) => {
  const { t } = useI18nContext();

  const isButtonDisabled = disabled;
  const isOptionComponentDisabled = disabled;

  const enableDarkMode = useExperiment(FEATURE_FLAGS.APP_DARKMODE);

  const icons =
    enableDarkMode && getDarkModePreferred() ? darkModeIcons : lightModeIcons;
  const dispatch = useDispatch();

  const onChange = useCallback(
    (_event, checked: boolean) => {
      setChecked(checked);
    },
    [setChecked]
  );

  const iconSrc = icons[iconName];

  const handleShowHalfSheet = useCallback(
    (event) => {
      event.preventDefault();
      if (!disabled) {
        openInfoSheet();
      }
    },
    [disabled, openInfoSheet]
  );

  const disabledClickHandler = () => {
    if (!isButtonDisabled) {
      dispatch(shopActions.setDeclineAllFintechOptions(false));
    }
  };

  return (
    <Box
      my={2}
      pr={4}
      className={clsx("fintech-selection-card", "uber", {
        selected: checked,
      })}
    >
      <FormControlLabel
        labelPlacement="start"
        disabled={isButtonDisabled}
        onClick={disabledClickHandler}
        control={
          useCheckbox ? (
            <Checkbox
              className="checkbox"
              checked={checked}
              onChange={onChange}
              disabled={isButtonDisabled}
              color="default"
            />
          ) : (
            <Button
              disableElevation
              className={clsx("cfar-select-button", {
                disabled: isButtonDisabled,
              })}
              onClick={onClick}
            >
              {checked
                ? t("fintechSelection.edit")
                : t("fintechSelection.select")}
            </Button>
          )
        }
        label={
          <Box
            mr={2}
            className={clsx("fintech-label", {
              disabled: isOptionComponentDisabled,
            })}
            onClick={handleShowHalfSheet}
          >
            {iconSrc && (
              <img
                alt="fintech type icon"
                className={clsx("fintech-icon", {
                  "src-svg-disabled": isOptionComponentDisabled,
                })}
                src={iconSrc}
              />
            )}
            <Box>
              <Typography variant="body1" className="fintech-option-title">
                {title}
              </Typography>
              <Typography variant="caption" className="fintech-description">
                {description}
              </Typography>
              <Box className="passenger-price">
                <Typography
                  variant="caption"
                  className={clsx("per-price-text")}
                >
                  {pricePerPax}
                </Typography>
                <img
                  alt="info icon"
                  src={icons.info}
                  className={clsx("info-icon", {
                    "src-svg-disabled": isOptionComponentDisabled,
                  })}
                />
              </Box>
            </Box>
          </Box>
        }
      />
    </Box>
  );
};
